import React, { type ReactNode } from "react";
import { ThemeProvider } from "styled-components";
import GlobalStyles from "./globals";

const breakpoints = {
  xs: "320px",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
};

export const devices = {
  xs: `(min-width: ${breakpoints.xs})`,
  sm: `(min-width: ${breakpoints.sm})`,
  md: `(min-width: ${breakpoints.md})`,
  lg: `(min-width: ${breakpoints.lg})`,
  xl: `(min-width: ${breakpoints.xl})`,
  "2xl": `(min-width: ${breakpoints["2xl"]})`,
};

export const theme = {
  colors: {
    // Brand
    primary: "var(--primary)",
    primaryFg: "var(--primary-fg)",
    primaryHover: "var(--primary-hover)",
    secondaryBg: "var(--secondary-bg)",
    secondaryBgHover: "var(--secondary-bg-hover)",
    accent: "var(--accent)",
    senneOrange: "var(--senne-orange)",
    senneOrangeLight: "var(--senne-orange-light)",
    senneAccent: "var(--senne-accent)",
    senneAccentSoft: "var(--senne-accent-soft)",
    senneCream: "var(--senne-cream)",
    senneCharcoal: "var(--senne-charcoal)",
    senneWhite: "var(--senne-white)",
    // Ação em TEXTO sobre fundo claro. `primary` (#ec6726) só passa AA em texto
    // grande/bold ou como fundo sólido; em texto pequeno o contraste é este
    // (DESIGN.md §2.1). As vars já existiam em tokens.css sem porta no theme.
    link: "var(--link)",
    linkHover: "var(--link-hover)",
    // Surface / Foreground
    bg: "var(--bg)",
    surface: "var(--surface)",
    surfaceWarm: "var(--surface-warm)",
    fg1: "var(--fg-1)",
    fg2: "var(--fg-2)",
    fg3: "var(--fg-3)",
    fg4: "var(--fg-4)",
    border: "var(--border)",
    borderStrong: "var(--border-strong)",
    // Neutral ramp
    gray50: "var(--gray-50)",
    gray100: "var(--gray-100)",
    gray200: "var(--gray-200)",
    gray300: "var(--gray-300)",
    gray400: "var(--gray-400)",
    gray500: "var(--gray-500)",
    gray600: "var(--gray-600)",
    gray700: "var(--gray-700)",
    gray800: "var(--gray-800)",
    gray900: "var(--gray-900)",
    // Semantic
    success: "var(--success)",
    successSoft: "var(--success-soft)",
    danger: "var(--danger)",
    dangerSoft: "var(--danger-soft)",
    warning: "var(--warning)",
    warningSoft: "var(--warning-soft)",
    info: "var(--info)",
    infoSoft: "var(--info-soft)",
    // Tints
    tintOrange50: "var(--tint-orange-50)",
    tintOrange100: "var(--tint-orange-100)",
    tintAccent10: "var(--tint-accent-10)",
    // Legacy compat (deprecation gradual em C-2)
    text: "var(--fg-1)",
    textGray: "var(--fg-2)",
    gray: "var(--border-strong)",
    background: "var(--bg)",
    blackBtn: "var(--gray-800)",
    secondary: "var(--accent)",
    tertiary: "var(--primary)",
    error: "var(--danger)",
    white: "#ffffff",
    black: "#000000",
    // Compat p/ componentes Carlão (LabReview) migrados de staging
    borderSubtle: "var(--border)",
    card: "var(--surface)",
    errorBorder: "var(--danger)",
    errorInk: "var(--danger)",
    errorTint: "var(--danger-soft)",
    focusRing: "rgba(236,103,38,0.35)",
    primaryDark: "var(--primary-hover)",
    primaryTintMedium: "var(--secondary-bg-hover)",
    primaryTintWeak: "var(--secondary-bg)",
    successInk: "var(--success)",
    successInkMedium: "var(--success-ink-medium)",
    successInkStrong: "var(--success-ink-strong)",
    successInkDeep: "var(--success-ink-deep)",
    successTint: "var(--success-soft)",
    surfaceMuted: "var(--gray-50)",
    warningBorder: "var(--warning)",
    warningInk: "var(--warning)",
    warningInkStrong: "var(--warning-ink-strong)",
    warningInkDeep: "var(--warning-ink-deep)",
    warningSubtle: "var(--warning-soft)",
    warningTint: "var(--warning-soft)",
    dangerInkStrong: "var(--danger-ink-strong)",
    dangerInkDeep: "var(--danger-ink-deep)",
    infoInkDeep: "var(--info-ink-deep)",
  },
  spacing: {
    1: "var(--space-1)",
    2: "var(--space-2)",
    3: "var(--space-3)",
    4: "var(--space-4)",
    5: "var(--space-5)",
    6: "var(--space-6)",
    8: "var(--space-8)",
    10: "var(--space-10)",
    12: "var(--space-12)",
    16: "var(--space-16)",
    24: "var(--space-24)",
    // Aliases semânticos — compat p/ componentes Carlão (LabReview) migrados de staging
    sm: "var(--space-2)",
    md: "var(--space-4)",
    lg: "var(--space-6)",
    xl: "var(--space-8)",
    "2xl": "var(--space-10)",
    "3xl": "var(--space-12)",
    "4xl": "var(--space-16)",
    "5xl": "var(--space-24)",
  },
  // Compat p/ componentes Carlão (LabReview) migrados de staging
  borderRadius: {
    sm: "var(--radius-sm)",
    xl: "var(--radius-xl)",
    full: "var(--radius-pill)",
  },
  zIndex: {
    modal: 1000,
    overlay: 999,
    // The floating list of a field (select, combobox, date, tooltip) renders outside the page and must open above
    // every modal layer: `ui/GenericModal` 19999, `ui/Modal` 99999 and its close button 100000 (spec 20261104-100000
    // AC-3, measured 2026-11-04). It sat at 9999 and opened behind "Recusar acréscimo".
    popover: 100100,
    // The reading stage (spec 20261104-160000): Carlão's video lies under the page, above the shell's background and
    // under its solid header (100) and aside (10001); the page's frame sits just above it.
    scene: 0,
    overScene: 1,
  },
  radii: {
    xs: "var(--radius-xs)",
    sm: "var(--radius-sm)",
    md: "var(--radius-md)",
    lg: "var(--radius-lg)",
    xl: "var(--radius-xl)",
    "2xl": "var(--radius-2xl)",
    "3xl": "var(--radius-3xl)",
    pill: "var(--radius-pill)",
  },
  shadows: {
    sm: "var(--shadow-sm)",
    md: "var(--shadow-md)",
    lg: "var(--shadow-lg)",
    xl: "var(--shadow-xl)",
    primary: "var(--shadow-primary)",
    accent: "var(--shadow-accent)",
    focusRing: "var(--focus-ring)",
    switchThumb: "var(--shadow-switch-thumb)",
    // Compat p/ componentes Carlão (LabReview/CarlaoWizard) migrados de staging
    card: "var(--shadow-md)",
    modal: "var(--shadow-xl)",
  },
  // Control sizes with no match on the spacing scale (P20EE-210).
  sizes: {
    focusOutline: "var(--focus-outline-width)",
    focusOutlineOffset: "var(--focus-outline-offset)",
    switchWidth: "var(--switch-width)",
    switchHeight: "var(--switch-height)",
    switchThumb: "var(--switch-thumb)",
    /** One height for every field and button (spec 20261028-130000 AC-2, AC-6). */
    controlHeight: "var(--control-height)",
    /** Carlão reading the order, and on a short screen (spec 20261028-131500 AC-5, AC-8). */
    carlaoFigure: "var(--carlao-figure)",
    carlaoFigureShort: "var(--carlao-figure-short)",
    /** Carlão beside his sentence, and on a short screen (spec 20261028-131500 AC-9). */
    carlaoAvatar: "var(--carlao-avatar)",
    carlaoAvatarShort: "var(--carlao-avatar-short)",
    /** The width a page's content stops at (`--content-max`). */
    contentMax: "var(--content-max)",
    /** A panel that drops from the header (the bell). */
    panelWidth: "var(--panel-width)",
  },
  motion: {
    easeStandard: "var(--ease-standard)",
    easeOut: "var(--ease-out)",
    durationFast: "var(--duration-fast)",
    durationBase: "var(--duration-base)",
    durationSlow: "var(--duration-slow)",
  },
  typography: {
    fontDisplay: "var(--font-display)",
    fontBody: "var(--font-body)",
    fontMono: "var(--font-mono)",
    fs: {
      display: "var(--fs-display)",
      h1: "var(--fs-h1)",
      h2: "var(--fs-h2)",
      h3: "var(--fs-h3)",
      h4: "var(--fs-h4)",
      lead: "var(--fs-lead)",
      body: "var(--fs-body)",
      sm: "var(--fs-sm)",
      xs: "var(--fs-xs)",
      "2xs": "var(--fs-2xs)",
      "3xs": "var(--fs-3xs)",
    },
    lh: {
      tight: "var(--lh-tight)",
      snug: "var(--lh-snug)",
      normal: "var(--lh-normal)",
      relaxed: "var(--lh-relaxed)",
    },
    fw: {
      regular: "var(--fw-regular)",
      medium: "var(--fw-medium)",
      semibold: "var(--fw-semibold)",
      bold: "var(--fw-bold)",
      black: "var(--fw-black)",
    },
    tracking: {
      eyebrow: "var(--tracking-eyebrow)",
      meta: "var(--tracking-meta)",
    },
  },
  // fontSize antigo — mantido para compat. Aponta para CSS vars novas onde fizer sentido.
  fontSize: {
    xs: "var(--fs-xs)",
    sm: "var(--fs-sm)",
    md: "var(--fs-body)",
    lg: "var(--fs-h4)",
    xl: "var(--fs-h3)",
    "2xl": "var(--fs-h2)",
    "3xl": "1.75rem",
    "4xl": "var(--fs-display)",
    "5xl": "2.5rem",
    "6xl": "3rem",
    "7xl": "3.75rem",
    "8xl": "4.5rem",
    "9xl": "6rem",
    "10xl": "8rem",
  },
  devices,
};

interface ThemeProps {
  children: ReactNode;
}

export const Theme: React.FC<ThemeProps> = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      {children}
      <GlobalStyles />
    </ThemeProvider>
  );
};
